import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CallFix, CrmAuth, Outcome } from './crmApi';
import LeadPanel, { fixWhat } from './LeadPanel';
import { Empty, Head, Spinner, fmtWhen, inputCls, purpleBg, useCrmMe } from './ui';

// ── Call fix requests ────────────────────────────────────────
// A salesperson logged a call wrongly (the wrong result, or on the wrong
// lead) and asked for it to be undone, from the lead's history. A Lead
// Manager, Sales Admin or the admin approves it: the call is crossed out, the
// lead goes back to how it was before it, and the call leaves the numbers;
// the salesperson then logs the right result. Or they turn it down, saying
// why. Mirrors the app's crm_requests_screen.dart.

const STATUS_STYLE: Record<CallFix['status'], { label: string; cls: string }> = {
  Pending:  { label: 'Waiting for approval', cls: 'bg-amber-100 text-amber-800' },
  Approved: { label: 'Approved: call undone', cls: 'bg-green-100 text-green-800' },
  Rejected: { label: 'Not approved', cls: 'bg-red-100 text-red-700' },
};
const StatusChip: React.FC<{ status: CallFix['status'] }> = ({ status }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[status].cls}`}>{STATUS_STYLE[status].label}</span>
);
const callOf = (r: CallFix) => `${r.outcome}${r.result ? ` → ${r.result}` : ''}`;
// "Brochure Sent → Poll Sent" → the Log call outcome to open with
const outcomeOf = (should: string) => should.split(' → ')[0] as Outcome;

const FixLine: React.FC<{ r: CallFix }> = ({ r }) => (
  <>
    <p className="text-sm text-gray-800"><b>{callOf(r)}</b> <span className="text-gray-400">· {r.callAt ? fmtWhen(r.callAt) : ''}</span></p>
    <p className="text-sm text-purple-900 font-semibold">{r.kind === 'wrong-lead' ? 'Logged on the wrong lead' : `Should be: ${r.should}`}</p>
    {r.reason && <p className="text-xs text-gray-600">“{r.reason}”</p>}
  </>
);

// The Lead Manager, Sales Admin or the admin: approve (undo the call) or turn down
export const CrmCallFixes: React.FC<{ auth: CrmAuth; embedded?: boolean; onChanged?: () => void }> = ({ auth, embedded, onChanged }) => {
  const { me } = useCrmMe(auth);
  const [status, setStatus] = useState<CallFix['status']>('Pending');
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.callFixes>> | null>(null);
  const [busy, setBusy] = useState('');
  const [turningDown, setTurningDown] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await crm.callFixes(auth, status)); } catch (e: any) { toast.error(e.message); }
  }, [auth, status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r: CallFix, approve: boolean) => {
    if (!approve && !note.trim()) { toast.error('Say why it is turned down'); return; }
    setBusy(r.id);
    try {
      await crm.decideCallFix(auth, r.id, approve, approve ? '' : note.trim());
      toast.success(approve ? `Undone. ${r.byName} will log the right result.` : `Turned down. ${r.byName} will be told.`);
      setTurningDown(null); setNote('');
      load();
      onChanged?.();
    } catch (e: any) { toast.error(e.message); load(); }
    finally { setBusy(''); }
  };

  const tabs: [CallFix['status'], string][] = [['Pending', 'Waiting'], ['Approved', 'Approved'], ['Rejected', 'Turned down']];
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Call fixes" sub="Calls a salesperson logged wrongly. Approving crosses the call out, puts the lead back as it was before it and takes the call out of the numbers; they then log the right result." />
      <div className="flex gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={status === k} onClick={() => setStatus(k)} className="px-3 py-1.5 rounded-lg text-xs font-bold"
            style={status === k ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
            {label} {data && <span className="opacity-60">{data.counts[k]}</span>}
          </button>
        ))}
      </div>
      {!data ? <Spinner /> : data.requests.length === 0 ? (
        <Empty icon={status === 'Pending' ? '✅' : '📭'} title={status === 'Pending' ? 'Nothing waiting' : 'None yet'} />
      ) : data.requests.map(r => (
        <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setOpen(r.leadId)} disabled={!r.lead} className="font-bold text-gray-800 enabled:hover:text-purple-700 text-left">#{r.leadNo} {r.leadName || 'No name'}</button>
                {r.status !== 'Pending' && <StatusChip status={r.status} />}
              </div>
              <p className="text-xs text-gray-500">From <b className="text-gray-700">{r.byName}</b> · asked {fmtWhen(r.createdAt)}{r.lead ? ` · now ${r.lead.stage}${r.lead.ownerName ? ` with ${r.lead.ownerName}` : ''}` : ''}</p>
            </div>
          </div>
          <FixLine r={r} />
          {r.status === 'Pending' && (
            <>
              {r.blocked
                ? <p className="text-xs rounded-xl px-3 py-2 bg-orange-50 text-orange-800">It can't be undone now: {r.blocked}. Turn it down, or fix the lead by hand.</p>
                : <p className="text-xs rounded-xl px-3 py-2 bg-gray-50 text-gray-600">Approving crosses the call out and puts the lead back to how it was before it. {r.byName} then logs the right result.</p>}
              {turningDown === r.id ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why not (they will see this)" className={inputCls} autoFocus aria-label="Why not" />
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => decide(r, false)} disabled={!!busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{busy === r.id ? 'Saving…' : 'Turn down'}</button>
                    <button onClick={() => { setTurningDown(null); setNote(''); }} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => decide(r, true)} disabled={!!busy || !!r.blocked} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>
                    {busy === r.id ? 'Undoing…' : 'Approve: undo the call'}
                  </button>
                  <button onClick={() => { setTurningDown(r.id); setNote(''); }} disabled={!!busy} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Turn down</button>
                  {r.lead && <button onClick={() => setOpen(r.leadId)} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Open the lead</button>}
                </div>
              )}
            </>
          )}
          {r.status === 'Approved' && <p className="text-xs text-green-700">Undone by {r.decidedByName} {r.decidedAt ? fmtWhen(r.decidedAt) : ''}</p>}
          {r.status === 'Rejected' && <p className="text-xs text-red-600">Turned down by {r.decidedByName}{r.note ? `: ${r.note}` : ''}</p>}
        </div>
      ))}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

// The salesperson: their requests, and the lead to log the right result on
export const CrmMyCallFixes: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const { me } = useCrmMe(auth);
  const [list, setList] = useState<CallFix[] | null>(null);
  const [open, setOpen] = useState<{ leadId: string; log?: Outcome } | null>(null);
  const load = useCallback(async () => {
    try { setList((await crm.myCallFixes(auth)).requests); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2">
      <div className="px-1">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">My call fixes {list && <span className="text-gray-400">· {list.length}</span>}</h2>
        <p className="text-xs text-gray-400">Logged a call wrongly? Open the lead and tap "Wrong? Ask to fix" on your last call.</p>
      </div>
      {!list ? <Spinner /> : list.length === 0 ? <Empty icon="🛠️" title="No call fixes" sub="Calls you ask to fix show up here." /> : list.map(r => (
        <div key={r.id} className="bg-white border border-gray-100 rounded-2xl px-4 py-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setOpen({ leadId: r.leadId })} className="font-bold text-gray-800 hover:text-purple-700 text-left">#{r.leadNo} {r.leadName || 'No name'}</button>
            <StatusChip status={r.status} />
          </div>
          <p className="text-xs text-gray-600">{callOf(r)} · {fixWhat(r)}</p>
          <p className="text-[11px] text-gray-400">Asked {fmtWhen(r.createdAt)}</p>
          {r.status === 'Rejected' && <p className="text-xs text-red-600">Not approved by {r.decidedByName}{r.note ? `: ${r.note}` : ''}</p>}
          {r.status === 'Approved' && (
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-green-700">Undone by {r.decidedByName}.</p>
              {r.kind === 'wrong-result' && (
                <button onClick={() => setOpen({ leadId: r.leadId, log: outcomeOf(r.should) })} className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white" style={purpleBg}>Log the right result</button>
              )}
            </div>
          )}
        </div>
      ))}
      {open && me && <LeadPanel auth={auth} leadId={open.leadId} me={me} logOutcome={open.log} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};
